// Smart Copy utility — builds brand-aware copy text for AssetCard

type CtxPkg = Record<string, string>;

export type ContextState = 'new' | 'old' | 'none';

const OLD_KEYS = ['product', 'voice', 'channel', 'customer'] as const;

export function detectContextState(
  isLoggedIn: boolean,
  ctxPkg: CtxPkg | null | undefined
): ContextState {
  if (!isLoggedIn || !ctxPkg) return 'none';
  if (typeof ctxPkg.business_name === 'string' && ctxPkg.business_name.trim()) {
    return 'new';
  }
  const hasOld = OLD_KEYS.some((k) => {
    const v = ctxPkg[k];
    return typeof v === 'string' && v.trim();
  });
  return hasOld ? 'old' : 'none';
}

interface AssetLike {
  asset_type: string;
  description: string | null;
  content: Record<string, unknown>;
}

interface PromptBlock {
  label?: string;
  content?: string;
  text?: string;
}

export function extractFullPrompt(asset: AssetLike): string {
  const c = asset.content || {};
  if (asset.asset_type === 'prompt_template') {
    if (typeof c.full_prompt === 'string' && c.full_prompt.trim()) return c.full_prompt;
    if (Array.isArray(c.blocks)) {
      const lines = (c.blocks as PromptBlock[])
        .map((b) => {
          const label = b?.label ?? '';
          const body = b?.content ?? b?.text ?? '';
          if (!body) return '';
          return label ? `[${label}]: ${body}` : String(body);
        })
        .filter(Boolean);
      if (lines.length) return lines.join('\n');
    }
    return asset.description ?? '';
  }
  switch (asset.asset_type) {
    case 'custom_instruction':
      return (c.instruction_text as string) || '';
    case 'skill':
      return (c.skill_text as string) || '';
    case 'golden_keyword':
      return (c.ai_prompt as string) || '';
    case 'rag_pack':
      return (c.companion_prompt as string) || '';
    default:
      return asset.description ?? '';
  }
}

export interface CopyMeta {
  title?: string | null;
  description?: string | null;
}

function buildMetaHeader(meta: CopyMeta | undefined): string[] {
  if (!meta?.title) return [];
  const lines = [`# ${meta.title.trim()}`];
  if (meta.description && meta.description.trim()) {
    lines.push(`> ${meta.description.trim()}`);
  }
  lines.push('');
  return lines;
}

/**
 * Build a plain copy text with localized title/description header + body.
 * Used when there's no smart context (e.g. AssetDetail copy, live workshop copy).
 */
export function buildPlainCopyText(
  meta: CopyMeta | undefined,
  body: string,
  _language: 'th' | 'en'
): string {
  const header = buildMetaHeader(meta);
  if (header.length === 0) return body;
  return [...header, body].join('\n');
}

export function buildSmartCopyText(
  ctxPkg: CtxPkg,
  aiAnalysis: { positioning?: string } | null | undefined,
  fullPrompt: string,
  language: 'th' | 'en',
  meta?: CopyMeta
): string {
  const isEn = language === 'en';

  const sysLine = isEn
    ? 'You are an AI that creates content matched to the brand below.'
    : 'คุณคือ AI ที่ช่วยสร้าง content ให้ตรงกับแบรนด์ด้านล่าง';

  const brandHeader = isEn ? '## Brand Info' : '## ข้อมูลแบรนด์';
  const promptHeader = isEn ? '## Prompt' : '## คำสั่ง';

  const labels: Record<string, string> = isEn
    ? {
        business_name: 'Business',
        products: 'Products/Services',
        target_audience: 'Target Audience',
        brand_tone: 'Tone',
        channels: 'Channels',
        usp: 'USP',
        pain_solved: 'Problem Solved',
        brand_promise: 'Brand Promise',
        product: 'Products/Services',
        voice: 'Brand Voice',
        channel: 'Channels',
        customer: 'Target Customer',
      }
    : {
        business_name: 'ชื่อธุรกิจ',
        products: 'สินค้า/บริการ',
        target_audience: 'กลุ่มเป้าหมาย',
        brand_tone: 'โทน',
        channels: 'ช่องทาง',
        usp: 'จุดเด่น',
        pain_solved: 'ปัญหาที่แก้',
        brand_promise: 'สัญญาของแบรนด์',
        product: 'สินค้า/บริการ',
        voice: 'โทนเสียง',
        channel: 'ช่องทาง',
        customer: 'ลูกค้าเป้าหมาย',
      };

  const order = [
    'business_name',
    'products',
    'product',
    'target_audience',
    'customer',
    'brand_tone',
    'voice',
    'channels',
    'channel',
    'usp',
    'pain_solved',
    'brand_promise',
  ];

  const seen = new Set<string>();
  const brandLines: string[] = [];

  const pushLine = (key: string) => {
    if (seen.has(key)) return;
    const raw = ctxPkg[key];
    const val = Array.isArray(raw) ? (raw as unknown[]).join(', ') : raw;
    if (typeof val !== 'string' || !val.trim()) return;
    seen.add(key);
    const label = labels[key] || key;
    brandLines.push(`- ${label}: ${val.trim()}`);
  };

  for (const k of order) pushLine(k);
  for (const k of Object.keys(ctxPkg)) pushLine(k);

  if (aiAnalysis?.positioning && aiAnalysis.positioning.trim()) {
    const posLabel = isEn ? 'Positioning' : 'จุดยืน';
    brandLines.push(`- ${posLabel}: ${aiAnalysis.positioning.trim()}`);
  }

  return [
    ...buildMetaHeader(meta),
    sysLine,
    '',
    brandHeader,
    ...brandLines,
    '',
    '---',
    '',
    promptHeader,
    fullPrompt,
  ].join('\n');
}


